import { useState } from 'react';
import {
  Modal,
  ModalContent,
  Button,
  Input,
  TextArea,
  Control,
  Label,
  Select,
  SelectOption,
  PlusIcon,
} from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import { partnerKeysApi } from '@/api/partnerKeys';
import type { ImportPartnerKeyPayload } from '@/api/partnerKeys';

interface ImportKeyDialogProps {
  partnerId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const ImportKeyDialog = ({ partnerId, onClose, onSuccess }: ImportKeyDialogProps) => {
  const [role, setRole] = useState<ImportPartnerKeyPayload['role']>('partner_public');
  const [armoredPublic, setArmoredPublic] = useState('');
  const [vaultSecretPath, setVaultSecretPath] = useState('');

  const { mutate: importKey, isSubmitting } = useApiMutation<ImportPartnerKeyPayload, void>({
    mutationFn: async (data) => {
      await partnerKeysApi.import(partnerId, data);
    },
    successMessage: 'Key imported successfully',
    errorMessage: 'Failed to import key',
    onSuccess: () => onSuccess(),
  });

  const isValid = (() => {
    const trimmed = armoredPublic.trim();
    const beginIdx = trimmed.indexOf('-----BEGIN PGP PUBLIC KEY BLOCK-----');
    const endIdx = trimmed.indexOf('-----END PGP PUBLIC KEY BLOCK-----');
    if (beginIdx === -1 || endIdx === -1 || endIdx <= beginIdx) return false;
    // Must have a non-empty body between the header and footer markers.
    const body = trimmed.slice(beginIdx + '-----BEGIN PGP PUBLIC KEY BLOCK-----'.length, endIdx).trim();
    return body.length > 0;
  })();

  const handleSubmit = async () => {
    if (!isValid) return;
    await importKey({
      role,
      keyType: 'pgp',
      armoredPublic: armoredPublic.trim(),
      vaultSecretPath: role === 'honeywell_pair' ? vaultSecretPath.trim() || undefined : undefined,
    });
  };

  return (
    <Modal
      title="Import PGP Key"
      onModalClose={onClose}
      onBackdropClick={onClose}
      footerLeft={
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
      }
      footerRight={
        <Button
          variant="primary"
          icon={<PlusIcon />}
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Importing...' : 'Import Key'}
        </Button>
      }
    >
      <ModalContent padded>
        <div className="flex flex-col gap-3">
          <Control hideSpacing="bottom">
            <Label htmlFor="key-role">Role</Label>
            <Select
              id="key-role"
              value={role}
              onChange={(e) => setRole(e.target.value as ImportPartnerKeyPayload['role'])}
            >
              <SelectOption value="partner_public" label="Partner public key (encrypt outbound)">
                Partner public key (encrypt outbound)
              </SelectOption>
              <SelectOption value="honeywell_pair" label="Honeywell keypair (decrypt inbound)">
                Honeywell keypair (decrypt inbound)
              </SelectOption>
            </Select>
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="key-armored">Armored Public Key</Label>
            <TextArea
              id="key-armored"
              rows={8}
              className="font-mono text-xs"
              placeholder="-----BEGIN PGP PUBLIC KEY BLOCK-----..."
              value={armoredPublic}
              onChange={(e) => setArmoredPublic(e.target.value)}
              required
            />
          </Control>

          {role === 'honeywell_pair' && (
            <Control hideSpacing="bottom">
              <Label htmlFor="key-vault-path">Secret Key Vault Path</Label>
              <Input
                id="key-vault-path"
                placeholder="e.g., pgp-keys/dominion/honeywell-pair"
                value={vaultSecretPath}
                onChange={(e) => setVaultSecretPath(e.target.value)}
              />
            </Control>
          )}

          <p className="text-xs text-color-tertiary">
            Only the public half of any key is stored here. For a Honeywell keypair, the private
            half must already be provisioned in Vault at the path above before importing.
            Importing a new key of the same role automatically deactivates the previous one.
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ImportKeyDialog;
